import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { getProducts } from "@/lib/api";
import { IMG, CATEGORIES } from "@/data/site";
import DivisionHero from "@/components/DivisionHero";
import ProductCard from "@/components/ProductCard";
import QuoteCTA from "@/components/QuoteCTA";
import { Reveal, Kicker } from "@/components/Reveal";

const LIFESTYLE = [IMG.dining, IMG.outdoorBench, IMG.armchair];

export default function Furniture() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    getProducts({ division: "furniture" }).then(setProducts);
  }, []);

  return (
    <div data-testid="furniture-page">
      <DivisionHero
        kicker="Division 03 — Furniture"
        lines={["Kwila Outdoor", "Furniture"]}
        intro="Premium outdoor furniture built from Kwila timber and designed for Australian outdoor living — tables, chairs, benches, settings, sunloungers and accessories. Request pricing and a delivery quote in one step."
        image="/furniture-hero.png"
        imageAlt="Teak (Vitex) round table with cross-back chairs in a dining nook"
        brochure={{ label: "Mr Kwila Furniture — Product Brochure", pdf: "/brochures/mr-kwila-furniture-brochure.pdf", cover: "/brochures/furniture-cover.png" }}
      />

      <section className="px-5 pb-20 md:px-10" data-testid="furniture-lifestyle">
        <div className="mx-auto grid max-w-[1500px] gap-6 md:grid-cols-3">
          {LIFESTYLE.map((src, i) => (
            <Reveal key={src} delay={i * 0.1}>
              <div className={`overflow-hidden rounded-sm ${i === 1 ? "md:mt-12" : ""}`}>
                <img
                  src={src}
                  alt="Kwila outdoor furniture lifestyle"
                  loading="lazy"
                  className="aspect-[4/5] w-full object-cover transition-transform duration-700 hover:scale-105"
                />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-stone/60 px-5 py-24 md:px-10" data-testid="furniture-categories">
        <div className="mx-auto max-w-[1500px]">
          <Reveal className="mb-8">
            <Kicker>The Collection</Kicker>
            <h2 className="mt-3 font-display text-3xl font-light tracking-tight md:text-5xl">Shop by category</h2>
          </Reveal>
          <div className="flex flex-wrap gap-3">
            {CATEGORIES.furniture.map((c) => (
              <Link
                key={c}
                to={`/shop?division=furniture&category=${encodeURIComponent(c)}`}
                data-testid={`furniture-category-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="rounded-full border border-ink/20 bg-cream px-6 py-3 text-sm font-semibold transition-colors hover:bg-ink hover:text-cream"
              >
                {c}
              </Link>
            ))}
          </div>
        </div>
      </section>


      <section className="px-5 py-24 md:px-10" data-testid="furniture-products">
        <div className="mx-auto max-w-[1500px]">
          <Reveal className="mb-12 flex flex-wrap items-end justify-between gap-6">
            <div>
              <Kicker>Built for Outside</Kicker>
              <h2 className="mt-3 font-display text-3xl font-light tracking-tight md:text-5xl">The range</h2>
            </div>
            <Link
              to="/shop?division=furniture"
              data-testid="furniture-view-all-link"
              className="flex items-center gap-2 rounded-full border border-ink/30 px-6 py-3 text-xs font-bold uppercase tracking-[0.14em] transition-colors hover:bg-ink hover:text-cream"
            >
              View All Furniture <ArrowRight size={14} />
            </Link>
          </Reveal>
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {products.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink px-5 py-20 text-cream md:px-10 md:py-24" data-testid="furniture-care-band">
        <div className="mx-auto flex max-w-[1500px] flex-col items-start justify-between gap-8 lg:flex-row lg:items-end">
          <Reveal>
            <Kicker dark>Before You Position It</Kicker>
            <h2 className="mt-3 max-w-2xl font-display text-3xl font-light leading-tight tracking-tight md:text-5xl">
              New Kwila releases tannins. Two minutes of care saves your paving.
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-cream/65">
              Hose new furniture down and let it weather on grass or gravel before placing it on tiles or
              concrete — then oil periodically to keep the colour rich. Assembly instruction sheets are
              available for every table, chair and bench on request.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/guides/tannin-bleed"
              data-testid="furniture-care-guide-link"
              className="flex items-center gap-2 rounded-full bg-cream px-7 py-3 text-xs font-bold uppercase tracking-[0.14em] text-ink transition-colors hover:bg-kwila hover:text-cream"
            >
              Read the Care Guide <ArrowUpRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>

      <QuoteCTA
        title="Furniture freight, sorted."
        copy="Outdoor furniture is bulky — tell us your delivery postcode and access notes and we will quote delivery, or arrange pickup from Archerfield, Brisbane."
      />
    </div>
  );
}
